import { useState } from "react"
import { Icon } from "@/components/Icon"
import { TIER_LABEL, type OioNextStep, type OioStepDecision } from "../lib/next-steps"
// The same card the appellate flow uses: an officer moving between an
// adjudication and an appeal reads one kind of next step, not two.
import "@/features/oia/oia.css"

/**
 * NEXT STEPS for an Order-in-Original: what the checks found, as things the
 * officer can act on (lib/next-steps.ts). One line a card; the check's own
 * explanation is behind "Details".
 *
 * Three actions, and none of them is the engine deciding for him:
 *  - the card's head takes him to the passage in the order;
 *  - "Fix with AI" hands the finding to the rewrite chat, which shows the
 *    change and waits for his approval before a word is saved;
 *  - "Keep as drafted" / "Mark resolved" is his own mark on the case.
 *
 * While a "settle before signing" step is open the order is not cleared:
 * Export and Filed wait for it (OIOWorkspace). Marking it either way clears it
 * — the veto is on signing an order he has not looked at, not on his judgment.
 */
export interface OioNextStepsPanelProps {
  steps: OioNextStep[]
  decisions: Record<string, OioStepDecision>
  /** A draft or a rewrite is running: nothing can be sent or marked. */
  busy: boolean
  onShow: (step: OioNextStep) => boolean
  onFix: (step: OioNextStep) => void
  onOpenAnalysis: () => void
  onDecide: (step: OioNextStep, decision: OioStepDecision | null) => void
}

const TIER_PILL: Record<OioNextStep["tier"], string> = {
  must: "pill pill--partial",
  should: "pill pill--neutral",
  look: "pill pill--neutral",
}

export function OioNextStepsPanel({ steps, decisions, busy, onShow, onFix, onOpenAnalysis, onDecide }: OioNextStepsPanelProps) {
  const [showSettled, setShowSettled] = useState(false)
  const open = steps.filter((s) => !decisions[s.id])
  const settled = steps.filter((s) => decisions[s.id])

  if (steps.length === 0) {
    return (
      <div className="oia-empty" data-testid="oio-next-steps-empty">
        No next steps. The checks on this order found nothing for you to settle; read it through and export when you
        are satisfied.
      </div>
    )
  }

  return (
    <div className="oia-panel__body" data-testid="oio-next-steps" style={{ padding: "12px 14px" }}>
      {open.length === 0 && <div className="oia-empty">Every next step is settled.</div>}
      {open.map((step) => (
        <StepCard
          key={step.id}
          step={step}
          decision={null}
          busy={busy}
          onShow={onShow}
          onFix={onFix}
          onOpenAnalysis={onOpenAnalysis}
          onDecide={onDecide}
        />
      ))}
      {settled.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn--quiet btn--sm" aria-expanded={showSettled} onClick={() => setShowSettled((v) => !v)}>
            <Icon name={showSettled ? "chevdown" : "chevright"} size={13} /> Settled ({settled.length})
          </button>
          {showSettled &&
            settled.map((step) => (
              <StepCard
                key={step.id}
                step={step}
                decision={decisions[step.id]}
                busy={busy}
                onShow={onShow}
                onFix={onFix}
                onOpenAnalysis={onOpenAnalysis}
                onDecide={onDecide}
              />
            ))}
        </div>
      )}
    </div>
  )
}

function StepCard({
  step,
  decision,
  busy,
  onShow,
  onFix,
  onOpenAnalysis,
  onDecide,
}: {
  step: OioNextStep
  decision: OioStepDecision | null
} & Pick<OioNextStepsPanelProps, "busy" | "onShow" | "onFix" | "onOpenAnalysis" | "onDecide">) {
  const [detailed, setDetailed] = useState(false)
  const [notInOrder, setNotInOrder] = useState(false)
  const settled = decision !== null
  const head = <div className="oia-card__title">{step.title}</div>

  return (
    <article
      className={"oia-card" + (step.tier === "must" && !settled ? " oia-card--high" : "") + (settled ? " oia-card--resolved" : "")}
      data-testid={`oio-step-${step.id}`}
    >
      <div className="oia-card__head">
        <div style={{ flex: 1, minWidth: 0 }}>
          {step.section ? (
            <button
              type="button"
              className="oia-card__locate"
              onClick={() => setNotInOrder(!onShow(step))}
              title="Show where this is in the order"
              data-testid="oio-step-locate"
            >
              {head}
              <span className="oia-card__where">
                <Icon name="arrowr" size={11} />{" "}
                {notInOrder ? "Not in the order" : `Show section ${step.section} in the order`}
              </span>
            </button>
          ) : (
            head
          )}
          {step.detail && (
            <button type="button" className="linklike oia-card__more" aria-expanded={detailed} onClick={() => setDetailed((v) => !v)}>
              {detailed ? "Less" : "Details"}
            </button>
          )}
          <div className="oia-card__meta">
            <span className={TIER_PILL[step.tier]}>{TIER_LABEL[step.tier]}</span>
            {step.count > 1 && <span className="pill pill--neutral">{step.count} places</span>}
            {settled && <span className="pill pill--neutral">{decision === "kept" ? "Kept as drafted" : "Resolved"}</span>}
          </div>
        </div>
      </div>
      {detailed && step.detail && (
        <div className="oia-card__body">
          <div>{step.detail}</div>
          {step.found && <div className="oia-quote">In the order: “{step.found}”</div>}
        </div>
      )}
      <div className="oia-card__foot">
        {settled ? (
          <button className="btn btn--quiet btn--sm" disabled={busy} onClick={() => onDecide(step, null)}>
            Reopen
          </button>
        ) : (
          <>
            {step.source === "citations" ? (
              <button className="btn btn--primary btn--sm" onClick={onOpenAnalysis}>
                <Icon name="chart" size={14} /> Open Analysis
              </button>
            ) : (
              <button
                className="btn btn--primary btn--sm"
                disabled={busy}
                onClick={() => onFix(step)}
                title="The chat proposes the change and waits for your approval before it is saved"
              >
                <Icon name="message" size={14} /> Fix with AI
              </button>
            )}
            <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => onDecide(step, "resolved")}>
              Mark resolved
            </button>
            <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => onDecide(step, "kept")}>
              Keep as drafted
            </button>
          </>
        )}
      </div>
    </article>
  )
}
